import {
	alias,
	custom,
	deserialize,
	getDefaultModelSchema,
	identifier,
	list,
	object,
	serializable,
} from "serializr";
import { IJsonObject } from "../api";
import { Opportunity } from "../opportunities/Opportunity";
import { Project } from "./Project";

interface IProps {
	id?: number | null;
	name?: string;
	notes?: string;
	amount?: number;
	amountPaid?: number;
	amountPending?: number;
	amountAllocated?: number;
	amountAvailable?: number;
	hourlyRate?: number | null;
	reportCurrency?: string;
	exchangeRate?: number | null;
	reportAmount?: number | null;
	hoursTotal?: number | null;
	hoursUsed?: number | null;
	hoursAvailable?: number | null;
	reportTitle?: string | null;
	reportToken?: string | null;
	reportUrl?: string | null;
	reportRecipients?: string[];
	reportLastSentAt?: string | null;
	accountId?: number;
	opportunities?: Opportunity[];
	projects?: Project[];
}

export class ProjectBudget {
	public static fromJson(json: IJsonObject): ProjectBudget {
		return deserialize(ProjectBudget, json);
	}

	public static fromJsonArray(json: IJsonObject[]): ProjectBudget[] {
		return json.map((user: IJsonObject) => this.fromJson(user));
	}

	@serializable(identifier())
	public id: number | null;

	@serializable
	public name: string;

	@serializable
	public notes: string;

	@serializable
	public amount: number;

	@serializable(alias("amount_paid"))
	public amountPaid: number;

	@serializable(alias("amount_pending"))
	public amountPending: number;

	@serializable(alias("amount_allocated"))
	public amountAllocated: number;

	@serializable(alias("amount_available"))
	public amountAvailable: number;

	@serializable(alias("hourly_rate"))
	public hourlyRate: number | null;

	// Amounts are stored in USD; the report can be shown in CAD.
	@serializable(alias("report_currency"))
	public reportCurrency: string;

	@serializable(alias("exchange_rate"))
	public exchangeRate: number | null;

	@serializable(alias("report_amount"))
	public reportAmount: number | null;

	@serializable(alias("hours_total"))
	public hoursTotal: number | null;

	@serializable(alias("hours_used"))
	public hoursUsed: number | null;

	@serializable(alias("hours_available"))
	public hoursAvailable: number | null;

	@serializable(alias("report_title"))
	public reportTitle: string | null;

	@serializable(alias("report_token"))
	public reportToken: string | null;

	@serializable(alias("report_url"))
	public reportUrl: string | null;

	// Null when no recipients have been configured, so normalise to an array.
	@serializable(
		alias(
			"report_recipients",
			custom(
				(value: string[]) => value,
				(value: any) => (Array.isArray(value) ? value : []),
			),
		),
	)
	public reportRecipients: string[];

	@serializable(alias("report_last_sent_at"))
	public reportLastSentAt: string | null;

	@serializable(alias("account_id"))
	public accountId: number;

	@serializable(list(object(Opportunity)))
	public opportunities: Opportunity[];

	public projects: Project[];

	constructor({
		id = 1,
		name = "",
		notes = "",
		amount = 0,
		amountPending = 0,
		amountPaid = 0,
		amountAllocated = 0,
		amountAvailable = 0,
		hourlyRate = null,
		reportCurrency = "USD",
		exchangeRate = null,
		reportAmount = null,
		hoursTotal = null,
		hoursUsed = null,
		hoursAvailable = null,
		reportTitle = null,
		reportToken = null,
		reportUrl = null,
		reportRecipients = [],
		reportLastSentAt = null,
		accountId = 0,
		opportunities = [],
		projects = [],
	}: IProps = {}) {
		this.id = id;
		this.name = name;
		this.notes = notes;
		this.amount = amount;
		this.amountPending = amountPending;
		this.amountPaid = amountPaid;
		this.amountAllocated = amountAllocated;
		this.amountAvailable = amountAvailable;
		this.hourlyRate = hourlyRate;
		this.reportCurrency = reportCurrency;
		this.exchangeRate = exchangeRate;
		this.reportAmount = reportAmount;
		this.hoursTotal = hoursTotal;
		this.hoursUsed = hoursUsed;
		this.hoursAvailable = hoursAvailable;
		this.reportTitle = reportTitle;
		this.reportToken = reportToken;
		this.reportUrl = reportUrl;
		this.reportRecipients = reportRecipients;
		this.reportLastSentAt = reportLastSentAt;
		this.accountId = accountId;
		this.opportunities = opportunities;
		this.projects = projects;

		getDefaultModelSchema(ProjectBudget).props.projects = list(
			object(Project),
		);
	}
}
